import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Link } from 'react-router-dom';
import useWindowWidth from '../hooks/useWindowWidth';
import '../components/RevenueDesktop.css';
import '../components/MobileApp.css';

const formatCurrency = (value) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('pt-BR', { timeZone: 'UTC' });

const getStatusBadgeClass = (status) =>
    status === 'Paga' ? 'success' : status === 'Fechada' ? 'secondary' : 'warning';

const EMPTY_FILTERS = {
    credit_card_id: '',
    due_date_start: '',
    due_date_end: '',
    reference_month_start: '',
    reference_month_end: '',
    status: '',
    amount_min: '',
    amount_max: '',
};

function InvoicesPage() {
    const [invoices, setInvoices] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(true);
    const [creditCards, setCreditCards] = useState([]);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [showFilters, setShowFilters] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const width = useWindowWidth();
    const isDesktop = width >= 768;

    const fetchInvoices = (url = '/invoices', currentFilters = filters) => {
        setLoading(true);
        const urlObj = new URL(url, window.location.origin);
        const page = urlObj.searchParams.get('page');
        const params = new URLSearchParams(currentFilters);
        if (page) params.set('page', page);
        const pathname = urlObj.pathname.replace(/^\/api/, '');
        const fullUrl = `${pathname}?${params.toString()}`;

        api.get(fullUrl)
            .then(response => {
                setInvoices(response.data.data);
                setPagination(response.data);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching invoices:', error);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchInvoices();
        api.get('/credit-cards').then(r => setCreditCards(r.data)).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const handleApplyFilters = () => {
        fetchInvoices('/invoices');
        if (!isDesktop) setShowFilterModal(false);
    };

    const handleClearFilters = () => {
        setFilters(EMPTY_FILTERS);
        fetchInvoices('/invoices', EMPTY_FILTERS);
        if (!isDesktop) setShowFilterModal(false);
    };

    const decodePaginationLabel = (label) =>
        label
            .replace(/&laquo;/g, '«')
            .replace(/&raquo;/g, '»')
            .replace(/&amp;/g, '&');

    const hasActiveFilters = Object.values(filters).some(v => v !== '');

    // ── Desktop filters panel ──
    const renderDesktopFilters = () => (
        <div className="rv-filters-panel">
            <div className="rv-filter-group">
                <label>Cartão</label>
                <select name="credit_card_id" value={filters.credit_card_id} onChange={handleFilterChange} className="rv-filter-select">
                    <option value="">Todos</option>
                    {creditCards.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </div>
            <div className="rv-filter-group">
                <label>Vencimento De</label>
                <input type="date" name="due_date_start" value={filters.due_date_start} onChange={handleFilterChange} className="rv-filter-input" />
            </div>
            <div className="rv-filter-group">
                <label>Vencimento Até</label>
                <input type="date" name="due_date_end" value={filters.due_date_end} onChange={handleFilterChange} className="rv-filter-input" />
            </div>
            <div className="rv-filter-group">
                <label>Referência De</label>
                <input type="date" name="reference_month_start" value={filters.reference_month_start} onChange={handleFilterChange} className="rv-filter-input" />
            </div>
            <div className="rv-filter-group">
                <label>Referência Até</label>
                <input type="date" name="reference_month_end" value={filters.reference_month_end} onChange={handleFilterChange} className="rv-filter-input" />
            </div>
            <div className="rv-filter-group">
                <label>Status</label>
                <select name="status" value={filters.status} onChange={handleFilterChange} className="rv-filter-select">
                    <option value="">Todos</option>
                    <option value="Aberta">Aberta</option>
                    <option value="Paga">Paga</option>
                    <option value="Fechada">Fechada</option>
                </select>
            </div>
            <div className="rv-filter-group">
                <label>Valor Mínimo</label>
                <input type="number" name="amount_min" value={filters.amount_min} onChange={handleFilterChange} className="rv-filter-input" placeholder="0,00" />
            </div>
            <div className="rv-filter-group">
                <label>Valor Máximo</label>
                <input type="number" name="amount_max" value={filters.amount_max} onChange={handleFilterChange} className="rv-filter-input" placeholder="0,00" />
            </div>
            <div className="rv-filter-actions">
                <button className="rv-btn rv-btn-primary" onClick={handleApplyFilters}>Aplicar</button>
                <button className="rv-btn rv-btn-outline" onClick={handleClearFilters}>Limpar</button>
            </div>
        </div>
    );

    // ── Mobile filters ──
    const renderMobileFilters = () => (
        <div style={{ padding: '0 1.2rem 1rem' }}>
            <div className="mapp-filter-field">
                <label className="mapp-filter-label">Cartão</label>
                <select name="credit_card_id" value={filters.credit_card_id} onChange={handleFilterChange} className="mapp-filter-select">
                    <option value="">Todos</option>
                    {creditCards.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </div>
            <div className="mapp-filter-row">
                <div className="mapp-filter-field">
                    <label className="mapp-filter-label">Vencimento De</label>
                    <input type="date" name="due_date_start" value={filters.due_date_start} onChange={handleFilterChange} className="mapp-filter-input" />
                </div>
                <div className="mapp-filter-field">
                    <label className="mapp-filter-label">Vencimento Até</label>
                    <input type="date" name="due_date_end" value={filters.due_date_end} onChange={handleFilterChange} className="mapp-filter-input" />
                </div>
            </div>
            <div className="mapp-filter-row">
                <div className="mapp-filter-field">
                    <label className="mapp-filter-label">Referência De</label>
                    <input type="date" name="reference_month_start" value={filters.reference_month_start} onChange={handleFilterChange} className="mapp-filter-input" />
                </div>
                <div className="mapp-filter-field">
                    <label className="mapp-filter-label">Referência Até</label>
                    <input type="date" name="reference_month_end" value={filters.reference_month_end} onChange={handleFilterChange} className="mapp-filter-input" />
                </div>
            </div>
            <div className="mapp-filter-field">
                <label className="mapp-filter-label">Status</label>
                <select name="status" value={filters.status} onChange={handleFilterChange} className="mapp-filter-select">
                    <option value="">Todos</option>
                    <option value="Aberta">Aberta</option>
                    <option value="Paga">Paga</option>
                    <option value="Fechada">Fechada</option>
                </select>
            </div>
            <div className="mapp-filter-row">
                <div className="mapp-filter-field">
                    <label className="mapp-filter-label">Valor Mínimo</label>
                    <input type="number" name="amount_min" value={filters.amount_min} onChange={handleFilterChange} className="mapp-filter-input" placeholder="0,00" />
                </div>
                <div className="mapp-filter-field">
                    <label className="mapp-filter-label">Valor Máximo</label>
                    <input type="number" name="amount_max" value={filters.amount_max} onChange={handleFilterChange} className="mapp-filter-input" placeholder="0,00" />
                </div>
            </div>
            <div className="mapp-filter-actions">
                <button className="mapp-btn-apply" onClick={handleApplyFilters}>Aplicar</button>
                <button className="mapp-btn-clear" onClick={handleClearFilters}>Limpar</button>
            </div>
        </div>
    );

    // ── Desktop pagination ──
    const renderDesktopPagination = () => {
        if (!pagination?.links) return null;
        return (
            <div className="rv-pagination-wrap">
                <ul className="pagination">
                    {pagination.links.map((link, index) => (
                        <li key={index} className={`page-item ${link.active ? 'active' : ''} ${!link.url ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => link.url && fetchInvoices(link.url)}>
                                {decodePaginationLabel(link.label)}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    // ── Mobile pagination ──
    const renderMobilePagination = () => {
        if (!pagination?.links) return null;
        return (
            <div className="mapp-pagination">
                {pagination.links.map((link, index) => (
                    <button
                        key={index}
                        className={`mapp-page-btn ${link.active ? 'active' : ''}`}
                        onClick={() => link.url && fetchInvoices(link.url)}
                        disabled={!link.url}
                    >
                        {decodePaginationLabel(link.label)}
                    </button>
                ))}
            </div>
        );
    };

    // ── Mobile view ──
    const renderMobileView = () => (
        <div>
            <div className="mapp-topbar">
                <span className="mapp-topbar-title">Faturas</span>
                <button
                    className={`mapp-filter-btn ${hasActiveFilters ? 'active' : ''}`}
                    onClick={() => setShowFilterModal(true)}
                >
                    ⊞ Filtros{hasActiveFilters ? ' ●' : ''}
                </button>
            </div>

            {showFilterModal && (
                <>
                    <div className="mapp-sheet-overlay" onClick={() => setShowFilterModal(false)} />
                    <div className="mapp-sheet">
                        <div className="mapp-sheet-handle" />
                        <div className="mapp-sheet-header">
                            <span className="mapp-sheet-title">Filtrar Faturas</span>
                            <button className="mapp-sheet-close" onClick={() => setShowFilterModal(false)}>✕</button>
                        </div>
                        <div className="mapp-sheet-body">
                            {renderMobileFilters()}
                        </div>
                    </div>
                </>
            )}

            {loading ? (
                <div className="mapp-empty"><p>Carregando...</p></div>
            ) : invoices.length === 0 ? (
                <div className="mapp-empty">
                    <div className="mapp-empty-icon">🧾</div>
                    <p>Nenhuma fatura encontrada</p>
                </div>
            ) : invoices.map(invoice => (
                <div className="card mb-3" key={invoice.id} style={{ margin: '0 0.75rem 0.75rem' }}>
                    <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <strong className="text-primary" style={{ fontSize: '1.1em' }}>{formatCurrency(invoice.total_amount)}</strong>
                            <span className={`badge bg-${getStatusBadgeClass(invoice.status)}`}>{invoice.status}</span>
                        </div>
                        <div className="small mb-1">
                            <span className="text-muted">Cartão:</span> {invoice.credit_card?.name}
                        </div>
                        <div className="small mb-1">
                            <span className="text-muted">Referência:</span> {formatDate(invoice.reference_month)}
                        </div>
                        <div className="small mb-2">
                            <span className="text-muted">Vencimento:</span> {formatDate(invoice.due_date)}
                        </div>
                        <Link to={`/invoices/${invoice.id}`} className="btn btn-info btn-sm w-100">
                            Ver Detalhes
                        </Link>
                    </div>
                </div>
            ))}

            {renderMobilePagination()}
        </div>
    );

    // ── Desktop view ──
    const renderDesktopView = () => (
        <div>
            <div className="rv-filter-bar">
                <button className="rv-btn rv-btn-outline" onClick={() => setShowFilters(!showFilters)}>
                    {showFilters ? '✕ Ocultar Filtros' : '⊞ Filtros'}
                </button>
            </div>

            {showFilters && renderDesktopFilters()}

            <div className="rv-table-wrapper">
                {loading ? (
                    <p style={{ padding: '1rem' }}>Carregando...</p>
                ) : (
                    <table className="rv-table">
                        <thead>
                            <tr>
                                <th>Cartão</th>
                                <th>Mês de Referência</th>
                                <th>Valor Total</th>
                                <th>Vencimento</th>
                                <th>Status</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.length === 0 ? (
                                <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>Nenhuma fatura encontrada</td></tr>
                            ) : invoices.map(invoice => (
                                <tr key={invoice.id}>
                                    <td>{invoice.credit_card?.name}</td>
                                    <td>{formatDate(invoice.reference_month)}</td>
                                    <td>{formatCurrency(invoice.total_amount)}</td>
                                    <td>{formatDate(invoice.due_date)}</td>
                                    <td>
                                        <span className={`badge bg-${getStatusBadgeClass(invoice.status)}`}>
                                            {invoice.status}
                                        </span>
                                    </td>
                                    <td className="rv-td-actions">
                                        <div className="rv-td-actions-inner">
                                            <Link to={`/invoices/${invoice.id}`} className="rv-btn rv-btn-edit">
                                                Ver Detalhes
                                            </Link>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                {renderDesktopPagination()}
            </div>
        </div>
    );

    return (
        <div className={isDesktop ? 'container mt-4' : ''}>
            {isDesktop && (
                <div className="rv-page-header">
                    <div className="rv-page-header-title">
                        <h1>Faturas</h1>
                        <p>Acompanhe as faturas dos cartões de crédito</p>
                    </div>
                </div>
            )}

            {isDesktop ? renderDesktopView() : renderMobileView()}
        </div>
    );
}

export default InvoicesPage;
